import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import Button from '../../Components/Button'
import Layout from '../GuestOrder/Layout'

const MAX_BODY_LENGTH = 2000

function Star({ filled, className = 'w-12 h-12' }) {
  return (
    <svg
      className={`${className} ${filled ? 'text-yellow-400' : 'text-white/30'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.98 10.1c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  )
}

const RATING_LABELS = ['', 'Poor', 'Not great', 'OK', 'Good', 'Excellent']

export default function Show({ account, order, review, token, errors = {} }) {
  const [rating, setRating] = useState(0)
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = (e) => {
    e.preventDefault()
    if (!rating || submitting) return
    setSubmitting(true)
    router.post(`/review/${token}`, { review: { rating, body } }, {
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <>
      <Head title={`Review your order - ${account.name}`} />
      <Layout account={account}>
        <div className="min-h-screen px-4 py-10 max-w-md mx-auto text-white">
          {review ? (
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-6">Thanks for your feedback!</h1>
              <div className="flex justify-center mb-4" role="img" aria-label={`You rated your order ${review.rating} out of 5 stars`}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} filled={star <= review.rating} className="w-10 h-10" />
                ))}
              </div>
              {review.body && (
                <p className="text-white/80 italic mb-6">&ldquo;{review.body}&rdquo;</p>
              )}
              <p className="text-white/70 mb-8">
                {review.rating <= 3
                  ? `We're sorry it wasn't perfect — the team at ${account.name} has been notified and may be in touch.`
                  : `Your review has been shared with ${account.name}. See you next time!`}
              </p>
              <Button href={`/${account.slug}/order`} variant="secondary" theme="dark" size="lg" fullWidth className="rounded-xl">
                Order Food &amp; Drinks
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} aria-label="Review form">
              <h1 className="text-2xl font-bold mb-2 text-center">How was your order?</h1>
              <p className="text-white/70 text-center mb-8">
                Order {order.order_code || order.order_number} from {account.name}
              </p>

              <div className="flex justify-center mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="p-1 focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    aria-pressed={rating === star}
                  >
                    <Star filled={star <= rating} />
                  </button>
                ))}
              </div>
              <p className="text-center text-white/70 h-6 mb-6" aria-live="polite">
                {rating ? RATING_LABELS[rating] : 'Tap a star to rate'}
              </p>

              <div className="mb-6">
                <label htmlFor="review-body" className="block text-white text-sm font-medium mb-2">
                  Tell us more (optional)
                </label>
                <textarea
                  id="review-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="What was great? What could be better?"
                  maxLength={MAX_BODY_LENGTH}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent text-gray-900"
                />
              </div>

              {(errors.rating || errors.base) && (
                <div className="bg-red-500 text-white px-4 py-3 rounded-lg mb-4" role="alert">
                  {errors.rating || errors.base}
                </div>
              )}

              <p className="text-white/60 text-sm mb-4 text-center">
                Reviews can&apos;t be edited once submitted.
              </p>

              <Button
                type="submit"
                disabled={!rating || submitting}
                variant="secondary"
                theme="dark"
                size="lg"
                fullWidth
                className="rounded-xl"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          )}
        </div>
      </Layout>
    </>
  )
}
